import React, { useState } from "react";
import { addVariantToCart } from "@/lib/shopifyCart";

/**
 * GoldSingleCard — image-driven visual card for a Premium $20+ Gold
 * Crumple single. Used on /shop and on /shop/gold-singles.
 *
 * Layout notes:
 *   • Image + title + "View item" all navigate to `product_url` in the
 *     SAME TAB (no target="_blank") — mobile back returns cleanly.
 *   • Padding/button tightened for a slimmer catalog scroll.
 */
const AVAIL = {
  IN_STOCK: { label: "IN STOCK", cls: "bg-emerald-100 text-emerald-800" },
  SOLD_OUT: { label: "SOLD OUT", cls: "bg-neutral-200 text-neutral-700" },
};

export default function GoldSingleCard({ product }) {
  const {
    sticker_code, subject_name, player_name, country_code, sticker_type,
    product_title, image_url, price, availability,
    shopify_variant_id, product_url,
  } = product || {};

  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState("");

  const soldOut = availability === "SOLD_OUT";
  const canAdd  = Boolean(shopify_variant_id) && !soldOut;
  const displayName = player_name || subject_name || product_title || "";
  const avail = soldOut ? AVAIL.SOLD_OUT : AVAIL.IN_STOCK;

  const typeChip =
    sticker_type === "fwc"        ? "FWC" :
    sticker_type === "team-badge" ? "TEAM BADGE" :
                                    (country_code || "GOLD");

  let cta;
  if (!shopify_variant_id) cta = "UNAVAILABLE";
  else if (soldOut)        cta = "SOLD OUT";
  else if (busy)           cta = "ADDING…";
  else                     cta = "ADD TO CART";

  const onAdd = async () => {
    if (!canAdd || busy) return;
    setBusy(true); setErr("");
    const res = await addVariantToCart(shopify_variant_id, 1);
    setBusy(false);
    if (!res.ok) setErr("Could not open cart — please refresh.");
  };

  const linkProps = product_url ? { href: product_url } : {};

  const Image = (
    <div className="aspect-square bg-neutral-50 overflow-hidden flex items-center justify-center">
      {image_url ? (
        <img src={image_url} alt={displayName} loading="lazy"
             className="w-full h-full object-cover" />
      ) : (
        <div className="text-5xl" aria-hidden>✨</div>
      )}
    </div>
  );

  return (
    <div
      data-testid={`gold-single-card-${sticker_code || "x"}`}
      className="rounded-2xl border-2 border-[var(--ink)]/15 bg-white overflow-hidden flex flex-col shadow-[4px_4px_0_var(--panini-yellow)]"
    >
      {product_url ? (
        <a {...linkProps}
           data-testid={`gold-single-card-image-${sticker_code || "x"}`}
           aria-label={displayName}
           className="block">
          {Image}
        </a>
      ) : Image}

      <div className="p-2.5 flex flex-col gap-0.5 flex-1">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--smoke)]">
            {sticker_code || "GOLD"} · {typeChip}
          </div>
          <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${avail.cls}`}>{avail.label}</span>
        </div>
        {product_url ? (
          <a {...linkProps}
             className="font-display text-xs md:text-sm leading-tight line-clamp-2 min-h-[2.2em] hover:underline"
             data-testid={`gold-single-card-title-${sticker_code || "x"}`}>
            {displayName}
          </a>
        ) : (
          <div className="font-display text-xs md:text-sm leading-tight line-clamp-2 min-h-[2.2em]">
            {displayName}
          </div>
        )}
        <div className="text-base md:text-lg font-display leading-none mt-0.5">
          {price || <span className="text-[var(--smoke)] text-xs">—</span>}
        </div>

        <div className="mt-auto flex flex-col gap-0.5 pt-1">
          <button
            type="button"
            onClick={onAdd}
            disabled={!canAdd || busy}
            aria-disabled={!canAdd || busy}
            className={`btn-yellow btn-yellow--slim w-full justify-center ${(!canAdd || busy) ? "opacity-60 cursor-not-allowed" : ""}`}
            data-variant-id={shopify_variant_id || ""}
            data-testid={`gold-single-card-cta-${sticker_code || "x"}`}
          >
            {cta}
          </button>
          {product_url ? (
            <a {...linkProps}
               className="text-[10px] text-[var(--smoke)] hover:text-[var(--ink)] underline underline-offset-2 text-center"
               data-testid={`gold-single-card-view-${sticker_code || "x"}`}>
              View item
            </a>
          ) : null}
          {err ? (
            <div role="alert" className="text-[10px] text-red-700 text-center"
                 data-testid={`gold-single-card-error-${sticker_code || "x"}`}>
              {err}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
